import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const articles = [
  {
    category: 'Resume Tips',   color: '#0066FF', bg: '#e8f0ff', icon: 'fa-file-alt',
    title: 'How to Write a Resume That Gets Noticed in 2024',
    desc: 'Learn the key elements of a modern resume that hiring managers actually read — from formatting to keywords.',
    readTime: '5 min read', date: 'Dec 2024',
    tips: ['Use a clean, ATS-friendly format', 'Tailor keywords to each job description', 'Quantify achievements with numbers', 'Keep it to 1-2 pages max'],
  },
  {
    category: 'Interview Prep', color: '#00C896', bg: '#e6fff6', icon: 'fa-comments',
    title: 'Top 10 Interview Questions & How to Answer Them',
    desc: 'Master the most common interview questions with proven frameworks that leave a lasting impression.',
    readTime: '8 min read', date: 'Dec 2024',
    tips: ['Use the STAR method for behavioural questions', 'Research the company thoroughly', 'Prepare 3-5 questions to ask', 'Practice out loud, not just in your head'],
  },
  {
    category: 'Career Growth',  color: '#f59e0b', bg: '#fff8e6', icon: 'fa-chart-line',
    title: 'How to Ask for a Promotion (And Actually Get It)',
    desc: 'A step-by-step guide to building your case, timing the conversation, and negotiating your next role.',
    readTime: '6 min read', date: 'Nov 2024',
    tips: ['Document your achievements regularly', 'Align your goals with company goals', 'Choose the right moment to ask', 'Be specific about what you want'],
  },
  {
    category: 'Job Search',     color: '#8b5cf6', bg: '#f3eeff', icon: 'fa-search',
    title: 'The Smart Job Search Strategy That Actually Works',
    desc: 'Stop applying to hundreds of jobs blindly. Here\'s a focused strategy that gets more callbacks.',
    readTime: '7 min read', date: 'Nov 2024',
    tips: ['Target 10-15 companies you love', 'Network before you need a job', 'Customise every application', 'Follow up after 5-7 days'],
  },
  {
    category: 'Salary',         color: '#ef4444', bg: '#fff0f0', icon: 'fa-rupee-sign',
    title: 'How to Negotiate Your Salary Without Losing the Offer',
    desc: 'Salary negotiation is expected — here\'s how to do it confidently and professionally.',
    readTime: '5 min read', date: 'Oct 2024',
    tips: ['Always negotiate — it\'s expected', 'Anchor high with a specific number', 'Know your market value first', 'Negotiate the full package, not just salary'],
  },
  {
    category: 'LinkedIn',       color: '#0077b5', bg: '#e8f4ff', icon: 'fab fa-linkedin-in',
    title: 'Build a LinkedIn Profile That Attracts Recruiters',
    desc: 'Your LinkedIn is your digital resume. Here\'s how to optimise every section to get found.',
    readTime: '6 min read', date: 'Oct 2024',
    tips: ['Use a professional headshot', 'Write a compelling headline', 'Add measurable results to experience', 'Get at least 5 recommendations'],
  },
];

const quickTips = [
  { icon: 'fa-clock',       color: '#0066FF', tip: 'Apply within 24 hours of a job posting — early applicants are 3x more likely to get interviews.' },
  { icon: 'fa-user-check',  color: '#00C896', tip: 'Tailor your resume for each job. Generic resumes get ignored by ATS systems.' },
  { icon: 'fa-handshake',   color: '#f59e0b', tip: '85% of jobs are filled through networking. Build relationships before you need them.' },
  { icon: 'fa-star',        color: '#8b5cf6', tip: 'Follow up after every interview with a personalised thank-you email within 24 hours.' },
];

export default function CareerAdvice() {
  const [activeCategory, setActiveCategory] = useState('All');
  const allCategories = ['All', ...new Set(articles.map(a => a.category))];

  const filtered = activeCategory === 'All' ? articles : articles.filter(a => a.category === activeCategory);

  return (
    <>
      <PageHeader title="Career Advice" breadcrumb="Career Advice" />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #0A1628 0%, #1a2d4f 100%)', padding: '60px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <span style={{ background: 'rgba(0,102,255,0.2)', color: '#60a5fa', fontSize: 12, fontWeight: 700, padding: '6px 16px', borderRadius: 50, letterSpacing: 1 }}>CAREER RESOURCES</span>
          <h2 style={{ color: '#fff', fontWeight: 800, fontSize: 34, margin: '16px 0 12px' }}>Expert Career Advice</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, maxWidth: 540, margin: '0 auto 32px' }}>
            Practical tips, guides and strategies to help you land your dream job and grow your career.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            {[{ num: '50+', label: 'Articles' }, { num: '10K+', label: 'Readers' }, { num: '4.9★', label: 'Rating' }].map((s, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 28px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{s.num}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Tips Bar */}
      <section style={{ background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '28px 0' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20 }} className="cat-stats-grid">
            {quickTips.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '16px', background: '#fafbff', borderRadius: 14, border: '1.5px solid #eef0f8' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: t.color + '18', color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                  <i className={`fa ${t.icon}`}></i>
                </div>
                <p style={{ fontSize: 12, color: '#555', lineHeight: 1.6, margin: 0 }}>{t.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="section-pad">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Latest Articles</span>
            <h2 className="section-title">Career Guides & Tips</h2>
            <p className="section-sub">Actionable advice from hiring experts and career coaches</p>
          </div>

          {/* Category Filter */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 40 }}>
            {allCategories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                style={{ padding: '8px 20px', borderRadius: 50, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'all .2s', background: activeCategory === cat ? '#0066FF' : '#f0f2f8', color: activeCategory === cat ? '#fff' : '#555', boxShadow: activeCategory === cat ? '0 4px 14px rgba(0,102,255,0.3)' : 'none' }}>
                {cat}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 28 }} className="cat-why-grid">
            {filtered.map((a, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 20, border: '1.5px solid #eef0f8', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', transition: 'all .25s', cursor: 'pointer' }} className="cat-why-card">
                {/* Card Header */}
                <div style={{ background: a.bg, padding: '28px 24px 20px', position: 'relative' }}>
                  <span style={{ background: a.color, color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 50, display: 'inline-block', marginBottom: 14 }}>
                    {a.category}
                  </span>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: '#fff', color: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    <i className={`fa ${a.icon}`}></i>
                  </div>
                  <h5 style={{ fontSize: 15, fontWeight: 700, color: '#0A1628', margin: 0, lineHeight: 1.4 }}>{a.title}</h5>
                </div>
                {/* Card Body */}
                <div style={{ padding: '20px 24px' }}>
                  <p style={{ fontSize: 13, color: '#888', lineHeight: 1.7, marginBottom: 16 }}>{a.desc}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
                    {a.tips.map((tip, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#555', marginBottom: 8 }}>
                        <i className="fa fa-check-circle" style={{ color: a.color, marginTop: 2, flexShrink: 0 }}></i>
                        {tip}
                      </li>
                    ))}
                  </ul>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, borderTop: '1px solid #f5f5f5' }}>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontSize: 11, color: '#aaa' }}><i className="fa fa-clock me-1"></i>{a.readTime}</span>
                      <span style={{ fontSize: 11, color: '#aaa' }}><i className="fa fa-calendar me-1"></i>{a.date}</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: a.color, cursor: 'pointer' }}>Read More <i className="fa fa-arrow-right ms-1"></i></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-left">
              <h2>Ready to Put This Advice to Work?</h2>
              <p>Browse thousands of live job listings and apply today.</p>
            </div>
            <div className="cta-right">
              <Link to="/job-list" className="cta-btn-white">Browse Jobs</Link>
              <Link to="/job-alerts" className="cta-btn-outline">Set Job Alerts</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
